<template>
  <div :class="padding">
    <div v-if="data" class="title">
      <h3 class="section-title">
        {{ data.name.toUpperCase() }}
      </h3>
      <div class="separator" />
    </div>
    <div class="flex flex-row gap-4 align-middle items-center">
      <div v-for="media in mediasToShow" :key="media" class="icon">
        <!-- TODO: Añadir aria-label Ex: aria-label="Ir a la página principal"-->
        <a
          v-if="media !== 'copylink'"
          :href="medias[media].href"
          class="hover:cursor-pointer inline-block"
          :aria-label="medias[media].label"
          target="_blank"
        >
          <w-icon
            :img="require('@/assets/images/' + medias[media].icon)"
            :size="size"
            :alt="medias[media].label"
          />
        </a>
        <button v-else aria-label="Copy button">
          <w-icon
            :img="require('@/assets/images/' + medias[media].icon)"
            :size="size"
            :alt="medias[media].label"
            @click="getUrl()"
          />
        </button>
      </div>
    </div>
    <p v-if="successLink" class="copied-successful">
      <span>
        <img
          :src="require('@/assets/images/ic-checked-topaz.svg')"
          :alt="$t('magazine.links.copySuccess')"
        />
      </span>
      {{ $t("magazine.links.copySuccess") }}
    </p>
  </div>
</template>

<script setup>
import WIcon from "@/components/general/ui/elements/WIcon.vue";
import { toRefs, computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  size: {
    type: Number,
    default: 25
  },
  padding: {
    type: String,
    default: "pb-4"
  },
  rounded: {
    type: Number,
    default: null
  },
  data: {
    type: Object,
    default: () => {}
  },
  mediasToShow: {
    type: Array,
    default: () => [
      "instagram",
      "tiktok",
      "youtube",
      "facebook",
      "twitter",
      "spotify",
      "web",
      "telegram",
      "soundcloud",
      "other"
    ]
  },
  instagram: {
    type: String,
    default: "https://www.instagram.com/wegowes/"
  },
  tiktok: {
    type: String,
    default: "https://www.tiktok.com/@wegowes?lang=es"
  },
  facebook: {
    type: String,
    default: "https://www.facebook.com/wegow.es/"
  },
  twitter: {
    type: String,
    default: "https://twitter.com/wegowes?lang=es"
  },
  youtube: {
    type: String,
    default: "https://www.youtube.com/channel/UCrftSfN7u5GNiHDy4bBtxHg"
  },
  spotify: {
    type: String,
    default: "https://open.spotify.com/user/wegow?si=5ea40e7fd51945de"
  },
  web: {
    type: String,
    default: "https://www.wegow.com/es-es/"
  },
  telegram: {
    type: String,
    default: ""
  },
  linkedin: {
    type: String,
    default: "https://es.linkedin.com/company/wegow"
  },
  whatsapp: {
    type: String,
    default: ""
  },
  soundcloud: {
    type: String,
    default: ""
  },
  copylink: {
    type: String,
    default: ""
  },
  other: {
    type: String,
    default: ""
  },
  share: {
    type: Boolean,
    default: false
  }
});

const {
  size,
  // rounded,
  mediasToShow,
  instagram,
  tiktok,
  facebook,
  twitter,
  youtube,
  spotify,
  web,
  whatsapp,
  telegram,
  linkedin,
  copylink,
  soundcloud,
  other
} = toRefs(props);

const { t } = useI18n();

const medias = computed(() => ({
  instagram: {
    icon: `ic-instagram${checkImage.value}`,
    label: !props.share
      ? t("wegow.arias.components.socialLinks.goTo", {
          social: "Instagram"
        })
      : t("wegow.arias.components.socialLinks.share", {
          social: "Instagram"
        }),
    href: instagram.value
  },
  tiktok: {
    icon: `ic-tiktok${checkImage.value}`,
    label: t("wegow.arias.components.socialLinks.goTo", {
      social: "Tiktok"
    }),
    href: tiktok.value
  },
  facebook: {
    icon: `ic-facebook${checkImage.value}`,
    label: !props.share
      ? t("wegow.arias.components.socialLinks.goTo", {
          social: "Facebook"
        })
      : t("wegow.arias.components.socialLinks.share", {
          social: "Facebook"
        }),
    href: facebook.value
  },
  twitter: {
    icon:
      checkImage.value === "-topaz.svg"
        ? `ic-x${checkImage.value}`
        : `ic-twitter${checkImage.value}`,
    label: !props.share
      ? t("wegow.arias.components.socialLinks.goTo", {
          social: "X"
        })
      : t("wegow.arias.components.socialLinks.share", {
          social: "X"
        }),
    href: twitter.value
  },
  youtube: {
    icon: `ic-youtube${checkImage.value}`,
    label: t("wegow.arias.components.socialLinks.goTo", {
      social: "Youtube"
    }),
    href: youtube.value
  },
  spotify: {
    icon: `ic-spotify${checkImage.value}`,
    label: t("wegow.arias.components.socialLinks.goTo", {
      social: "Spotify"
    }),
    href: spotify.value
  },
  web: {
    icon: `ic-web${checkImage.value}`,
    label: t("wegow.arias.components.socialLinks.goTo", {
      social: web.value
    }),
    href: web.value
  },
  telegram: {
    icon: `ic-telegram${checkImage.value}`,
    label: t("wegow.arias.components.socialLinks.share", {
      social: "Telegram"
    }),
    href: telegram.value
  },
  linkedin: {
    icon: `ic-linkedin${checkImage.value}`,
    label: !props.share
      ? t("wegow.arias.components.socialLinks.goTo", {
          social: "Linkedin"
        })
      : t("wegow.arias.components.socialLinks.share", {
          social: "Linkedin"
        }),
    href: linkedin.value
  },
  whatsapp: {
    icon: `ic-whatsapp${checkImage.value}`,
    label: t("wegow.arias.components.socialLinks.share", {
      social: "WhatsApp"
    }),
    href: whatsapp.value
  },
  copylink: {
    icon: `ic-copylink${checkImage.value}`,
    label: t("wegow.arias.components.socialLinks.copyLink")
  },
  soundcloud: {
    icon: `ic-sound-cloud${checkImage.value}`,
    label: t("wegow.arias.components.socialLinks.goTo", {
      social: "SoundCloud"
    }),
    href: soundcloud.value
  },
  other: {
    icon: `ic-other${checkImage.value}`,
    label: t("wegow.arias.components.socialLinks.goTo", {
      social: other.value
    }),
    href: other.value
  }
}));
const successLink = ref(false);
const getUrl = async () => {
  const textarea = document.createElement("textarea");
  textarea.value = location.href;
  textarea.style.position = "fixed";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
  successLink.value = true;
  setTimeout(() => {
    successLink.value = false;
  }, 1500);
};
const checkImage = computed(() => {
  if (props.rounded === 0) {
    return "-topaz.svg";
  } else if (props.rounded === 1) {
    return "-topaz-black-rounded.svg";
  } else if (props.rounded === 2) {
    return "-white.svg";
  } else {
    return "-grey.svg";
  }
});
// const getWtsa = url => {
//   const message = encodeURIComponent(location.href);
//   window.open(url + message);
// };
</script>

<style lang="scss" scoped>
.copied-successful {
  display: flex;
  margin-top: 2.6rem;
  color: $black;
  span {
    margin-right: 6px;
    img {
      margin: auto;
    }
  }
}
.icon {
  line-height: normal;
}
</style>
